import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  AppointmentListItem,
  AppointmentPastWeekResponse,
} from "../../../shared/api-types.js";
import { authenticatedApi } from "./api.js";
import { listMirrorPoll } from "./mirror-state.js";
import {
  appointmentsPastWeekApiRequest,
  deviceTimezone,
  keepsPreviousAppointmentRows,
  type AppointmentListSearch,
} from "./appointments.js";
import {
  currentWeekStart,
  pastWeekQueryKey,
  pastWeekSeed,
  type PastWeekBody,
  type PastWeekClock,
  type PastWeekRequest,
} from "./appointment-past-week.js";

type AvailablePastWeek = Extract<AppointmentPastWeekResponse, { status: "available" }>;
type UnavailablePastWeek = Extract<
  AppointmentPastWeekResponse,
  { status: "unavailable" }
>;

export interface PastWeekShown {
  status: "week";
  display: PastWeekClock;
  healthieCalendarUrl: string | null;
  week: string;
  appointments: AppointmentListItem[];
  hasEarlier: boolean;
  hasLater: boolean;
  /**
   * `loading`: another week (or toggle) is on its way and these rows are the
   * previous answer, dimmed. `error`: the requested week failed; the rows are
   * empty and the pager works relative to that week.
   */
  phase: "ready" | "loading" | "error";
  /** The week being loaded while `phase` is `loading`. */
  target: string;
  retry: () => void;
}

export type AppointmentPastWeek =
  | { status: "idle" }
  | { status: "loading" }
  /** F5b: failed before any week was known. */
  | { status: "first-error"; retry: () => void }
  | { status: "no_selection" }
  | { status: "unavailable"; reason: UnavailablePastWeek["reason"] }
  /** No history at all under the current status filter. */
  | { status: "empty"; display: PastWeekClock; healthieCalendarUrl: string }
  | PastWeekShown;

/**
 * A seeded opening week must survive the URL replace that names it; it is
 * fresh for this long so writing the week into the URL does not read again.
 */
export const PAST_WEEK_STALE_MS = 10_000;

/**
 * The Past week read (NO-767). Enabled only while view=past. The previous
 * week stays on screen as placeholder within one selection, which is the
 * dimmed loading state. With no week in the URL the answer names the opening
 * week; it seeds that week's entry and `onOpeningWeek` writes it into the URL.
 */
export function useAppointmentPastWeek(
  search: AppointmentListSearch,
  enabled: boolean,
  onOpeningWeek: (week: string) => void
): AppointmentPastWeek {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const request: PastWeekRequest = {
    week: search.week ?? null,
    showNonDefaultStatuses: search.showNonDefaultStatuses === true,
    timezone: deviceTimezone(),
    ...(search.rdId ? { rdId: search.rdId } : {}),
  };
  const query = useQuery({
    queryKey: pastWeekQueryKey(request),
    queryFn: () => {
      const api = appointmentsPastWeekApiRequest(request);

      return authenticatedApi<PastWeekBody>(getToken, api.path, api.init);
    },
    enabled,
    placeholderData: (previousData, previousQuery) =>
      keepsPreviousAppointmentRows(previousQuery?.queryKey, search.rdId)
        ? previousData
        : undefined,
    staleTime: PAST_WEEK_STALE_MS,
    gcTime: 0,
    // NO-917: a visit still waiting for its Healthie copy reads again.
    refetchInterval: (current) => listMirrorPoll(current.state.data),
  });
  const isFreshAnswer = enabled && !query.isPlaceholderData;
  const seed = isFreshAnswer && query.data ? pastWeekSeed(request, query.data) : null;
  const seedWeek = seed ? seed.key[1] : null;

  useEffect(() => {
    if (!seed || seedWeek === null) return;

    queryClient.setQueryData(seed.key, seed.body);
    onOpeningWeek(seedWeek);
    // Keyed by the week the seed names: one seed and one URL write per answer.
  }, [seedWeek]);

  if (!enabled) return { status: "idle" };

  return pastWeekState({
    data: query.data,
    isError: query.isError,
    isPlaceholderData: query.isPlaceholderData,
    requestedWeek: request.week,
    retry: () => {
      void query.refetch();
    },
  });
}

export function pastWeekState(input: {
  data: PastWeekBody | undefined;
  isError: boolean;
  isPlaceholderData: boolean;
  requestedWeek: string | null;
  retry: () => void;
  clock?: () => PastWeekClock;
}): AppointmentPastWeek {
  const { data, requestedWeek, retry } = input;

  if (input.isError) {
    if (requestedWeek === null) return { status: "first-error", retry };

    return failedWeek(requestedWeek, retry, input.clock);
  }

  if (!data) return { status: "loading" };

  if (data.data.status !== "available") return unshownState(data.data);

  return shownState(data.data, {
    loading: input.isPlaceholderData,
    target: requestedWeek,
    retry,
  });
}

function unshownState(
  data: Exclude<AppointmentPastWeekResponse, { status: "available" }>
): AppointmentPastWeek {
  if (data.status === "no_selection") return { status: "no_selection" };

  return { status: "unavailable", reason: data.reason };
}

function shownState(
  data: AvailablePastWeek,
  options: { loading: boolean; target: string | null; retry: () => void }
): AppointmentPastWeek {
  const display = { now: data.now, timezone: data.timezone };

  if (data.weekStart === null) {
    return { status: "empty", display, healthieCalendarUrl: data.healthieCalendarUrl };
  }

  return {
    status: "week",
    display,
    healthieCalendarUrl: data.healthieCalendarUrl,
    week: data.weekStart,
    appointments: data.appointments,
    hasEarlier: data.hasEarlier,
    hasLater: data.hasLater,
    phase: options.loading ? "loading" : "ready",
    target: options.target ?? data.weekStart,
    retry: options.retry,
  };
}

function deviceClock(): PastWeekClock {
  return { now: new Date().toISOString(), timezone: deviceTimezone() };
}

/**
 * The requested week failed and nothing answered for it, so the clock is the
 * device's own: the same zone the request sent.
 */
function failedWeek(
  week: string,
  retry: () => void,
  clock: () => PastWeekClock = deviceClock
): PastWeekShown {
  const display = clock();

  return {
    status: "week",
    display,
    healthieCalendarUrl: null,
    week,
    appointments: [],
    hasEarlier: true,
    hasLater: week < currentWeekStart(display.now, display.timezone),
    phase: "error",
    target: week,
    retry,
  };
}
